import AppStoreContext from 'contexts/AppStoreContext';
import { List } from 'immutable';
import * as React from 'react';
import { Project } from 'records/Project';
import { StudioProject } from 'records/StudioProject';
import { UpdateProject } from 'records/UpdateProject';
import { User } from 'records/User';
import { ExportItems, exportProjectData } from 'services/projectService';

export type ProjectManagerValue = {
  getProject: (projectid: string, fallbackValue?: Project) => Project;
  setProjectLocation: (projectid: string) => Promise<void>;
  loadProjectFromServer: (projectid: string) => Promise<void>;
  loadStudioProjects: () => Promise<void>;
  getSelectedProject: () => Project;
  updateProject: (updateProject: UpdateProject) => Promise<void>;
  refreshAccountProjects: (options: { clear: boolean }) => Promise<void>;
  exportProjectData: (projectid: string, items: ExportItems) => Promise<void>;
  studioProjects: List<StudioProject>;
  selectedUsers: List<User>;
};

type ProjectManagerConsumerProps = {
  children: (projectManager: ProjectManagerValue) => React.ReactNode;
};

export type WithProjectManagerProps = {
  projectManager: ProjectManagerValue;
};

const ProjectManagerContext = React.createContext<ProjectManagerValue | null>(
  null
);

export const ProjectManagerProvider = ProjectManagerContext.Provider;

export const AppProjectManagerProvider: React.FC = ({ children }) => {
  const { actions: appActions, getState: appState } =
    React.useContext(AppStoreContext);

  const projectManager: ProjectManagerValue = {
    setProjectLocation: async (projectid: string) => {
      await appActions.setProjectLocation(projectid);
    },
    loadProjectFromServer: async (projectid: string) => {
      await appActions.getProject(projectid);
    },
    loadStudioProjects: async () => {
      await appActions.getStudioProjects();
    },
    getSelectedProject: () => {
      return appState().getProject();
    },
    getProject: (projectid, fallbackValue) => {
      return appState().projects.get(projectid, fallbackValue) || new Project();
    },
    refreshAccountProjects: async ({ clear }) => {
      await appActions.getProjects(clear);
    },
    updateProject: async (updateProject) => {
      await appActions.updateProject(updateProject);
    },
    exportProjectData: async (projectid, items) => {
      await exportProjectData(projectid, items);
    },
    studioProjects: appState().studioProjects,
    selectedUsers: appState().selectedUsers,
  };

  return (
    <ProjectManagerProvider value={projectManager}>
      {children}
    </ProjectManagerProvider>
  );
};

export const useProjectManager = (): ProjectManagerValue => {
  const projectManager = React.useContext(ProjectManagerContext);

  if (projectManager === null) {
    throw new Error(
      'useProjectManager must be used within a ProjectManagerProvider'
    );
  }

  return projectManager;
};

export const ProjectManagerConsumer: React.FC<ProjectManagerConsumerProps> = ({
  children,
}) => {
  return (
    <ProjectManagerContext.Consumer>
      {(projectManager) => {
        if (projectManager === null) {
          throw new Error(
            'ProjectManagerConsumer must be used within a ProjectManagerProvider'
          );
        }
        return children(projectManager);
      }}
    </ProjectManagerContext.Consumer>
  );
};

export function withProjectManager<P>(
  WrappedComponent: React.ComponentType<P & WithProjectManagerProps>
): React.FC<P> {
  const WithProjectManagerWrapper: React.FC<P> = (props) => {
    return (
      <ProjectManagerConsumer>
        {(projectManager) => (
          // @ts-ignore - issue with @emotion/react https://github.com/emotion-js/emotion/issues/2169
          <WrappedComponent {...props} projectManager={projectManager} />
        )}
      </ProjectManagerConsumer>
    );
  };

  WithProjectManagerWrapper.displayName = `withProjectManager(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;

  return WithProjectManagerWrapper;
}
